'use-client';
import * as React from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import type {
  IStylableButtonProps,
  IStylableButtonImperativeActions,
} from '../../../StylableButton.types';
import stylableButtonSemanticClassNames from '../../../StylableButton.semanticClassNames';
import ButtonComponent from './Button';
import styles from './Button.module.scss';

export type LayoutClassType = 'physicalLayout' | 'logicalLayout';

type ButtonSkinProps = IStylableButtonProps & {
  withLogicalLayout?: boolean;
};

const ButtonSkin: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  ButtonSkinProps
> = (props, ref) => {
  const {
    className,
    isDisabled,
    direction,
    stylableButtonClassName,
    customClassNames = [],
    isMaxContent = false,
    isWrapText = false,
    fallbackDirectionClassName,
    withLogicalLayout = false,
  } = props;

  const [hasAnimations, setHasAnimations] = React.useState(false);
  const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const semanticClassNames =
    props.semanticClassNames || stylableButtonSemanticClassNames;

  const handleMouseEnter = React.useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    setHasAnimations(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    // Set timeout to remove animations after 0.4s (matching CSS transition duration)
    animationTimeoutRef.current = setTimeout(() => {
      setHasAnimations(false);
    }, 400);
  }, []);

  React.useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const layoutClassType: LayoutClassType = withLogicalLayout
    ? 'logicalLayout'
    : 'physicalLayout';

  const rootClassName = classNames(
    hasAnimations && styles.applyAnimations,
    styles.root,
    styles[layoutClassType],
    className,
    stylableButtonClassName,
    !direction && fallbackDirectionClassName,
    formatClassNames(semanticClassNames.root, ...customClassNames),
    {
      disabled: isDisabled,
      isMaxContent,
      isWrapText,
    },
  );

  const labelClassName = classNames(
    styles.label,
    formatClassNames(semanticClassNames.buttonLabel),
  );

  const iconClassName = classNames(
    styles.icon,
    formatClassNames(semanticClassNames.buttonIcon),
  );

  const linkClassName = styles.link;

  return (
    <ButtonComponent
      {...props}
      ref={ref}
      rootClassName={rootClassName}
      labelClassName={labelClassName}
      iconClassName={iconClassName}
      linkClassName={linkClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export default React.forwardRef(ButtonSkin);
